import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import fetchApi from '../../../services/fetchApi'
import BookingPatientsList from './BookingPatientsList'
import ConfirmDeleteDialog from './ConfirmDeleteDialog'
import DownloadButton from './DownloadButton'
import FilterRow from './FilterRow'
import { fetchDoctorsAndLocationsAndStatuses } from '../../../state/actions/patient-groups'
import styles from './styles.css'
import { renderAlert } from '../../../services/render-alert'

class BookedPatientsContainer extends Component {
  static propTypes = {
    activeLocation: PropTypes.shape({
      active: PropTypes.bool.isRequired,
      additional_recipients: PropTypes.arrayOf(PropTypes.object),
      city: PropTypes.string,
      country_code: PropTypes.string,
      created_at: PropTypes.string,
      doctor_id: PropTypes.string,
      earliest_same_day_reminder_hour: PropTypes.number,
      facebook_url: PropTypes.string,
      forwarding_email: PropTypes.string,
      google_url: PropTypes.string,
      id: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      name: PropTypes.string,
      office_email: PropTypes.string,
      office_phone: PropTypes.string,
      phone_numbers: PropTypes.arrayOf(PropTypes.object),
      practice_id: PropTypes.number,
      state: PropTypes.string,
      street_address: PropTypes.string,
      timezone: PropTypes.string,
      updated_at: PropTypes.string,
      website: PropTypes.string,
      yelp_url: PropTypes.string,
      zip: PropTypes.string,
    }),
    doctors: PropTypes.arrayOf(PropTypes.object).isRequired,
    hasDSO: PropTypes.bool.isRequired,
    locations: PropTypes.arrayOf(PropTypes.object).isRequired,
    loadDrsAndLocations: PropTypes.func.isRequired,
  }
  constructor(props) {
    super(props)
    const { activeLocation, hasDSO } = props
    this.state = {
      allPatientsLoaded: false,
      bookingPatients: [],
      currentPage: 1,
      bookingPatientIdToDelete: null,
      destroying: false,
      downloading: false,
      loading: true,
      searchAllLocations: false,
      selectedDoctor: '',
      sortColumn: 'created_at', // ['first_name', 'last_name', 'guardian_name', 'phone', 'email', 'created_at']
      sortDirection: 'desc', // ['asc, desc']
      selectedLocationId: (hasDSO && activeLocation) ? activeLocation.id : '',
    }
  }

  componentDidMount() {
    const { doctors, locations, loadDrsAndLocations } = this.props
    if (!doctors.length || locations.length) {
      loadDrsAndLocations()
    }
    this.fetchBookingPatients()
  }

  componentWillReceiveProps({ activeLocation: nextLoc }) {
    const nextId = nextLoc ? nextLoc.id : ''
    const { activeLocation } = this.props
    const id = activeLocation ? activeLocation.id : ''
    if (id !== nextId) {
      this.setState({
        searchAllLocations: false,
        selectedLocationId: nextId,
      }, () => this.refreshPatients(this.state.searchAllLocations),
      )
    }
  }

  fetchBookingPatients = async () => {
    try {
      const {
        bookingPatients,
        currentPage,
        searchAllLocations,
        selectedDoctor,
        selectedLocationId,
        sortColumn,
        sortDirection,
      } = this.state
      const queryParams = new URLSearchParams({
        page: currentPage,
        sort_column: sortColumn,
        sort_direction: sortDirection,
        doctor: selectedDoctor,
        location: searchAllLocations ? '' : selectedLocationId,
      })
      const bookingPatientsNextPage = await fetchApi(
        `/booking_patients?${queryParams}`,
        'GET',
      )
      this.setState({
        allPatientsLoaded: !bookingPatientsNextPage.length,
        bookingPatients: bookingPatients.concat(bookingPatientsNextPage),
        loading: false,
      })
    } catch (e) {
      renderAlert(e)
      this.setState({ loading: false })
    }
  }

  fetchAdditionalPatients = () => {
    const { currentPage } = this.state
    this.setState(
      {
        currentPage: currentPage + 1,
        loading: true,
      },
      this.fetchBookingPatients,
    )
  }

  destroyBookingPatient = async () => {
    try {
      const {
        bookingPatientIdToDelete,
        destroying,
        searchAllLocations,
       } = this.state
      if (destroying) return
      this.setState({ destroying: true })
      await fetchApi(
        `/booking_patients/${bookingPatientIdToDelete}`,
        'DELETE',
      )
      this.setState({
        bookingPatientIdToDelete: null,
        destroying: false,
      })
      renderAlert('Successfully deleted patient and appointment', 'success')
      this.refreshPatients(searchAllLocations)
    } catch (e) {
      renderAlert(e)
      this.setState({ destroying: false })
    }
  }

  updateFilterSelection = (e) => {
    // stop the select to bubble up to the tabs and render a blank page
    e.stopPropagation()
    this.setState(
      {
        [e.target.name]: e.target.value,
      },
      () => this.refreshPatients(this.state.searchAllLocations),
    )
  }

  handleAllLocationsCheckbox = (e) => {
    e.stopPropagation()
    if (this.state.loading) return
    const searchAll = e.target.checked
    this.refreshPatients(searchAll)
  }

  refreshPatients = (searchAll = false) => {
    this.setState(
      {
        allPatientsLoaded: false,
        bookingPatients: [],
        currentPage: 1,
        loading: true,
        searchAllLocations: searchAll,
      },
      () => this.fetchBookingPatients(),
    )
  }

  changeSort = (e) => {
    const sortColumn = e.target.id
    const {
      searchAllLocations,
      sortColumn: oldSortCol,
      sortDirection: oldSortDir,
    } = this.state
    const sortDirection =
      sortColumn === oldSortCol && oldSortDir === 'asc' ? 'desc' : 'asc'
    this.setState(
      {
        sortColumn,
        sortDirection,
      }, () => this.refreshPatients(searchAllLocations),
    )
  }

  handleDownload = async () => {
    this.setState({ downloading: true })
    try {
      const {
        selectedDoctor,
        selectedLocationId,
        sortColumn,
        sortDirection,
        searchAllLocations,
      } = this.state
      const queryParams = new URLSearchParams({
        sort_column: sortColumn,
        sort_direction: sortDirection,
        doctor: selectedDoctor,
        location: searchAllLocations ? '' : selectedLocationId,
      })

      const { data: downloadData } = await fetchApi(
        `/booking_patients/download?${queryParams}`,
        'GET',
      )
      this.setState({ downloading: false })
      this.downloadFile(downloadData)
    } catch (e) {
      renderAlert(e)
      this.setState({ downloading: false })
    }
  }

  downloadFile = (data) => {
    const url = window.URL.createObjectURL(new Blob([data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'booking_patients.csv')
    document.body.appendChild(link)
    link.click()
  }

  openDialog = bookingPatientId => this.setState({
    bookingPatientIdToDelete: bookingPatientId,
  })

  closeDialog = () => this.setState({ bookingPatientIdToDelete: null })

  render() {
    const {
      bookingPatientIdToDelete,
      bookingPatients,
      downloading,
      searchAllLocations,
      selectedDoctor,
      selectedLocationId,
    } = this.state
    const downloadDisabled = downloading || !bookingPatients.length
    return (
      <div className={styles.listContainer}>
        <h2 className={styles.centerText}>
          Patients Booked Through Online Scheduling
        </h2>
        <div className={styles.topRow}>
          <FilterRow
            {...this.props}
            handleAllLocationsCheck={this.handleAllLocationsCheckbox}
            searchAllLocations={searchAllLocations}
            selectedDoctor={selectedDoctor}
            selectedLocationId={selectedLocationId}
            updateFilterSelection={this.updateFilterSelection}
          />
          <DownloadButton
            disabled={downloadDisabled}
            downloading={downloading}
            handleDownload={this.handleDownload}
          />
        </div>
        <ConfirmDeleteDialog
          bookingPatientId={bookingPatientIdToDelete}
          closeDialog={this.closeDialog}
          handleConfirm={() => this.destroyBookingPatient()}
        />
        <BookingPatientsList
          {...this.state}
          changeSort={this.changeSort}
          openDialog={this.openDialog}
          fetchAdditionalPatients={this.fetchAdditionalPatients}
        />
      </div>
    )
  }
}

const mapStateToProps = ({ patientGroups: { doctors, locations } }) => ({
  doctors,
  locations,
})

const mapDispatchToProps = dispatch => ({
  loadDrsAndLocations: () => dispatch(fetchDoctorsAndLocationsAndStatuses()),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(BookedPatientsContainer)



// WEBPACK FOOTER //
// ./src/components/OnlineScheduling/BookingPatients/index.js